import {
  HeadingS,
  HeadingXS,
  IconInfoFill,
  IconLocationPinFill,
} from 'lib/design-system';
import { clearFlow, FlowId, track } from 'lib/sdks/analytics';
import { DirectMessageFunnelFlowSteps } from 'lib/sdks/analytics/types';
import {
  FunnellingBackButton,
  FunnellingFooter,
  HeaderSection,
  DeadEndArticle,
} from '../styles';
import {
  borderRadius,
  default700,
  neutral200,
  spacing1,
  spacing4,
} from 'lib/mortar';
import { useJob } from '../job.context';
import type { ScreenCommonProps } from '../types';
import { useGetJobLeads } from '@hooks/use-get-job-leads';
import { useCategoryLookup } from '../hooks/useCategoryLookup';
import {
  CompanyInfo,
  CompanyInfoContainer,
} from 'components/company-info/company-info';
import { ContactCtas } from 'lib/components/ContactCtas/ContactCtas';
import isBetweenHours from '@utils/is-between-hours';
import isWeekday from '@utils/is-weekday';
import { Stack } from 'components/layout/stack';
import styled from 'styled-components';
import ProgressLoader from 'components/Loader/ProgressLoader';
import { useTradeContext } from '../trade.context';
import type { Trade } from 'lib/sdks/search';
import { ErrorMessage } from 'lib/mortar/components/fields/field.style';
import { TextButton } from '../components/TextButton';

const CompanyCard = styled(Stack)`
  padding: ${spacing1} ${spacing4} ${spacing4};
  border: 1px solid ${neutral200};
  border-radius: ${borderRadius(2)};
`;

const ICONS = {
  locationPin: IconLocationPinFill,
  info: IconInfoFill,
} as const;

type DeadEndAltTradesIcons = keyof typeof ICONS;

export type DeadEndAltTradesProps = {
  heading: string;
  subheading: string;
  icon: DeadEndAltTradesIcons;
  excludeCompany?: number;
  onMessageCompany: () => void;
} & Pick<ScreenCommonProps, 'onBack'>;

export const DeadEndAltTrades = ({
  excludeCompany,
  heading,
  subheading,
  icon,
  onBack,
  onMessageCompany,
}: DeadEndAltTradesProps) => {
  const [tradeData, setTrade] = useTradeContext();
  const { job, setJobField } = useJob();
  const { categoryId, userProfile } = job;
  const postcode = userProfile?.address?.postcode?.toUpperCase();

  const Icon = ICONS[icon];

  const categoryLabel = useCategoryLookup(categoryId as number)?.label;

  const [loading, trades] = useGetJobLeads({
    categoryLabel,
    postcode,
    companyIds: excludeCompany !== undefined ? [excludeCompany] : undefined,
    options: {
      numberOfLeads: 4,
    },
  });

  const messageCompany = () => {
    if (tradeData) {
      clearFlow(
        FlowId.DIRECT_MESSAGE_FUNNELLING,
        undefined,
        DirectMessageFunnelFlowSteps.MESSAGE_ANYWAY,
      );
      setJobField('leads', [tradeData.companyId]);
      onMessageCompany();
    }
  };

  const handleContactTrade = (companyId: number) => {
    const trade = trades.find(t => t.companyId === companyId);
    if (!trade) {
      return;
    }

    track('global.message_trader', { trader_id: trade.companyId });
    setJobField('leads', [companyId]);
    setTrade(trade as Trade);
    onMessageCompany();
  };

  const onContactTrade = () => messageCompany();

  const prioritiseCalls = isBetweenHours(8, 18) && isWeekday();

  return (
    <DeadEndArticle>
      <HeaderSection direction="row" gap={3} alignItems="flex-start">
        <Icon size="medium" color={default700} />
        <Stack gap={2} alignItems="flex-start">
          <HeadingS as="h2">{heading}</HeadingS>
          <TextButton onClick={onContactTrade}>
            Contact this tradesperson anyway
          </TextButton>
        </Stack>
      </HeaderSection>

      <HeadingXS as="h3" color="primary">
        {subheading}
      </HeadingXS>
      <Stack gap={3}>
        {loading && <ProgressLoader />}
        {!loading &&
          trades.length > 0 &&
          trades.map(
            ({
              companyId,
              name,
              uniqueName,
              reviewsSummary,
              logoUrlTemplate,
              contact,
            }) => (
              <CompanyCard key={companyId}>
                <CompanyInfoContainer
                  direction="row"
                  alignItems="center"
                  gap={1}
                >
                  <CompanyInfo
                    headingAs="span"
                    name={name}
                    logoUrl={logoUrlTemplate}
                    postcode={postcode as string}
                    reviewsScore={reviewsSummary.recentMeanScore}
                    numberOfReviews={reviewsSummary.totalReviews}
                  />
                </CompanyInfoContainer>
                <ContactCtas
                  inlineOnDesktop
                  company={{ id: companyId, uniqueName }}
                  phone={{
                    label: contact.primary?.label,
                    number: contact.primary?.number,
                  }}
                  prioritiseCalls={prioritiseCalls}
                  showMessageButton
                  handleMessageClick={() => handleContactTrade(companyId)}
                />
              </CompanyCard>
            ),
          )}
        {!loading && !trades.length && (
          <ErrorMessage>No tradespeople found in this area.</ErrorMessage>
        )}
      </Stack>

      <FunnellingFooter>
        <FunnellingBackButton onClick={onBack} />
      </FunnellingFooter>
    </DeadEndArticle>
  );
};
